

import React from 'react';
import { TrendingUp, TrendingDown, DollarSign, Wallet } from 'lucide-react';
import { formatCurrency } from '@/lib/data';

interface FinancialMetrics {
  totalRevenue: number;
  totalExpenses: number;
  operationalExpenses: number;
  vendorPayments: number;
  netProfit: number;
  pendingPayments: number;
}

interface FinancialWidgetProps {
  metrics: FinancialMetrics;
  loading?: boolean;
}

export function FinancialWidget({ metrics, loading = false }: FinancialWidgetProps) {
  // Prefer backend-calculated netProfit; fall back to frontend calc only if backend value is missing
  const fallbackNetProfit = metrics.totalRevenue - metrics.totalExpenses;
  const netProfit = metrics.netProfit ?? fallbackNetProfit;

  // Sanity-check: warn if frontend and backend disagree by more than ₹1
  if (typeof window !== 'undefined' && Math.abs(netProfit - fallbackNetProfit) > 1) {
    console.warn('Dashboard netProfit mismatch: backend=' + netProfit + ', frontend=' + fallbackNetProfit);
  }

  const profitMargin = metrics.totalRevenue > 0
    ? ((netProfit / metrics.totalRevenue) * 100).toFixed(1)
    : '0.0';

  const items = [
    {
      label: 'Revenue',
      sublabel: 'from client payments',
      value: metrics.totalRevenue,
      icon: <DollarSign className="w-5 h-5" />,
      color: 'text-green-600',
      bg: 'bg-green-50',
    },
    {
      label: 'Operational',
      sublabel: 'expenses',
      value: metrics.operationalExpenses,
      icon: <Wallet className="w-5 h-5" />,
      color: 'text-red-600',
      bg: 'bg-red-50',
    },
    {
      label: 'Vendor',
      sublabel: 'payments',
      value: metrics.vendorPayments,
      icon: <Wallet className="w-5 h-5" />,
      color: 'text-orange-600',
      bg: 'bg-orange-50',
    },
    {
      label: 'Net Profit',
      sublabel: 'this month',
      value: netProfit,
      icon: netProfit >= 0 ?
        <TrendingUp className="w-5 h-5" /> :
        <TrendingDown className="w-5 h-5" />,
      color: netProfit >= 0 ? 'text-green-600' : 'text-red-600',
      bg: netProfit >= 0 ? 'bg-green-50' : 'bg-red-50',
    },
  ];

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-stone-800">Financial Overview</h3>
        <span className="text-xs text-stone-400">This month</span>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-20 bg-stone-100 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            {items.map((item, idx) => (
              <div key={idx} className={`${item.bg} rounded-xl p-3`}>
                <div className={`${item.color} mb-2`}>{item.icon}</div>
                <p className="text-xs font-medium text-stone-700">{item.label}</p>
                <p className="text-[10px] text-stone-400 mb-1">{item.sublabel}</p>
                <p className="text-sm font-bold text-stone-800">
                  {formatCurrency(item.value)}
                </p>
              </div>
            ))}
          </div>

          {/* Profit Margin Bar */}
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-100">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-xs text-stone-500 mb-0.5">Profit Margin</p>
                <p className="text-2xl font-bold text-amber-800">{profitMargin}%</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-stone-500 mb-0.5">Total Expenses</p>
                <p className="text-sm font-semibold text-red-700">
                  {formatCurrency(metrics.operationalExpenses + metrics.vendorPayments)}
                </p>
              </div>
            </div>
            <div className="w-full h-1.5 bg-amber-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-amber-500 rounded-full transition-all duration-500"
                style={{ width: `${Math.min(100, Math.max(0, parseFloat(profitMargin)))}%` }}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
